import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { STREAM_STATUS } from '../constants/status';
import { stripPracticeProposalTags } from '../utils/parsers/PracticeProposalParser';
import { parseToolEvents } from '../utils/parsers/toolEventParser';

/**
 * Custom hook for streaming response handlers
 * Extracts the complex streaming logic from useConversationEngine
 */
export function useStreamingHandlers({
  setStreamStatus,
  setStreamingMessage,
  addMessage,
  setActiveToolEvents,
  newlyCreatedConversationRef,
  cleanupStreamingResources,
  onStreamComplete,
}) {
  // Translation hook - instantiated internally for encapsulation
  const { t } = useTranslation(['chatbot']);

  // Track if we've received the first chunk for the current stream
  const isFirstChunkRef = useRef(true);

  // Frame coalescing. SSE frames arrive faster than paint and React 17 does NOT batch setState from
  // async stream callbacks, so each frame would be a full synchronous render pass. `chunk` is
  // CUMULATIVE (the full text so far, not a delta — the setStreamingMessage below REPLACES text), so
  // buffering only the latest chunk and flushing once per animation frame is lossless: any dropped
  // intermediate frame is fully represented by the next flush (tool-event markers accumulate in the
  // cumulative text too, so no started/completed event is lost).
  const pendingChunkRef = useRef(null);
  const rafIdRef = useRef(null);

  // Apply the latest buffered cumulative chunk to the streaming message + tool events. Runs as the
  // scheduled animation-frame callback, and synchronously from handleComplete/handleError (so the
  // final frame's tool events are applied exactly as the old per-chunk path did). Cancelling the
  // scheduled frame here makes the direct invocation consume it (cancelling the currently-executing
  // rAF id is a spec'd no-op).
  const flushPendingChunk = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    const chunk = pendingChunkRef.current;
    if (chunk === null) return;
    pendingChunkRef.current = null;

    // Parse tool events from the (cumulative) chunk
    const { cleanText, events } = parseToolEvents(chunk);

    // Filter out reference markers to prevent showing raw JSON during streaming, and practice
    // proposal tags (also a half-arrived one): the proposal card only renders on the final message.
    const displayText = stripPracticeProposalTags(cleanText.replace(/\[CONTENT_REFERENCES\][\s\S]*?\[\/CONTENT_REFERENCES\]/g, ''));

    // Process tool events
    events.forEach((event) => {
      if (event.status === 'started') {
        setActiveToolEvents((prev) => {
          // Check if a tool with the same name already exists
          const existingToolIndex = prev.findIndex((e) => e.toolName === event.toolName);

          if (existingToolIndex >= 0) {
            // Update existing tool event instead of adding a new one
            const updated = [...prev];
            updated[existingToolIndex] = { ...event, id: prev[existingToolIndex].id };
            return updated;
          } else {
            // Add new tool event
            return [...prev, { ...event, id: `${event.toolName}-${Date.now()}` }];
          }
        });
      } else if (event.status === 'completed' || event.status === 'failed') {
        setActiveToolEvents((prev) => prev.map((e) => (e.toolName === event.toolName ? { ...e, status: event.status, result: event.result } : e)));
      }
    });

    // Use functional update to avoid stale state issues
    setStreamingMessage((prev) => {
      // A cleanup (conversation switch, new chat, unmount, error) may have nulled the bubble between
      // scheduling and this flush. Do NOT resurrect a ghost bubble by spreading `null` into { text }.
      if (prev === null) return null;
      // Skip update if text is the same to avoid unnecessary rerenders
      if (prev.text === displayText) return prev;
      return {
        ...prev,
        text: displayText,
      };
    });
  }, [setActiveToolEvents, setStreamingMessage]);

  // Cancel-and-drop any buffered frame. MUST run before every setStreamingMessage(null) on a cleanup
  // path so a late rAF cannot resurrect the bubble or replay tool events onto a torn-down stream.
  const cancelPendingChunk = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    pendingChunkRef.current = null;
  }, []);

  // Begin a new stream: re-arm the first-chunk gate and enter CONNECTING. Owning the reset HERE (at
  // start) instead of inside the termination handlers means a stream that ends abnormally — an error,
  // or a cleanup/cancel that never reaches handleComplete — can't leave the gate stuck at false; the
  // next stream always re-arms it and will transition to STREAMING on its first chunk.
  const handleStreamStart = useCallback(() => {
    isFirstChunkRef.current = true;
    setStreamStatus(STREAM_STATUS.CONNECTING);
  }, [setStreamStatus]);

  const handleChunk = useCallback(
    (chunk) => {
      // Set status to STREAMING on first chunk — kept SYNCHRONOUS so the UI leaves CONNECTING
      // immediately. Text/tool-event parsing is deferred to the coalesced flush (lossless: see above).
      if (isFirstChunkRef.current) {
        setStreamStatus(STREAM_STATUS.STREAMING);
        isFirstChunkRef.current = false;
      }

      // Buffer the latest cumulative chunk; schedule a single flush for the next animation frame.
      pendingChunkRef.current = chunk;
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(flushPendingChunk);
      }
    },
    [setStreamStatus, flushPendingChunk]
  );

  const handleComplete = useCallback(
    (fullText, botMessageId, globalChatContext, conversationForMessage, realMessageId = null) => {
      // Apply any still-buffered frame BEFORE replacing the bubble — the old per-chunk path had
      // already applied it on arrival, so dropping it would lose the final sub-frame tool events
      // (e.g. a "completed" marker landing right before the complete event).
      flushPendingChunk();

      // Clean up streaming resources
      cleanupStreamingResources();

      setStreamStatus(STREAM_STATUS.COMPLETE);

      // Transition to IDLE after a brief delay to show completion state
      setTimeout(() => {
        setStreamStatus(STREAM_STATUS.IDLE);
      }, 100);

      // Parse and clean final text from any remaining tool events
      const { cleanText } = parseToolEvents(fullText);

      // Use real message ID from backend if available, otherwise fall back to temp ID
      const finalMessageId = realMessageId || botMessageId;

      // Update the streaming message with final text
      const finalMessage = {
        id: finalMessageId,
        text: cleanText,
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: false,
        context: globalChatContext,
      };

      // Replace streaming message with final message
      setStreamingMessage(null);
      addMessage(finalMessage);

      // Clear the newly created conversation tracking since we now have messages
      if (newlyCreatedConversationRef.current === conversationForMessage) {
        newlyCreatedConversationRef.current = null;
      }

      // Clear active tool events after a short delay to show completion
      setTimeout(() => {
        setActiveToolEvents([]);
      }, 1000);

      // Notify that streaming is complete for this conversation
      if (onStreamComplete && conversationForMessage) {
        onStreamComplete(conversationForMessage);
      }
    },
    [flushPendingChunk, cleanupStreamingResources, setStreamStatus, setStreamingMessage, addMessage, setActiveToolEvents, newlyCreatedConversationRef, onStreamComplete]
  );

  const handleError = useCallback(
    (error, currentConversationId, setStreamingMessage, setStreamStatus, streamControllerRef) => {
      // Apply any still-buffered frame first (matches the old per-chunk-on-arrival behavior), which
      // also consumes the scheduled rAF so a late flush cannot clobber the error text below.
      flushPendingChunk();

      // Stream errors emitted by the SSE `event: error` frame are tagged in
      // streamProcessors so we can render a generic message and ignore the
      // raw `data:` payload (which may be a technical provider error).
      const isStreamError = error?.name === 'SSEStreamError';

      // Check if this is a conversation creation error
      const isConversationCreationError = !isStreamError && (error?.message?.includes('conversation') || error?.message?.includes('Failed to create') || !currentConversationId);

      // Set error state
      setStreamStatus(STREAM_STATUS.ERROR);
      streamControllerRef.current = null;

      // Extract translated error message
      let errorMessage;

      // Customize message based on error type
      if (isStreamError) {
        errorMessage = t ? t('chat_stream_error_generic') : 'An error occurred while generating the response. Please try again in a few moments.';
      } else if (isConversationCreationError) {
        errorMessage = t ? t('Unable to create conversation. Please try again later.') : 'Unable to create conversation. Please try again later.';
      } else {
        // Generic error message with retry prompt
        errorMessage = t ? t('Error occurred. Want to retry?') : 'Oops! It seems an error occurred. Want to retry?';
      }

      // Update UI to show error
      setStreamingMessage((prev) => ({
        ...prev,
        text: errorMessage,
        isError: true,
        isStreaming: false,
      }));

      return { isConversationCreationError };
    },
    [t, flushPendingChunk]
  );

  return {
    handleStreamStart,
    handleChunk,
    handleComplete,
    handleError,
    cancelPendingChunk,
  };
}
